import { useCallback } from 'react';
import {
  Field,
  TextInput,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { generateReferenceId } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import { COMPANY_PROFILE_FIELDS } from '../../constants/accountSetup.constants';
import { isCompanyProfileFieldRequired } from '../../utils/companyProfile';
import styles from './CompanyIdField.module.scss';

/**
 * Props:
 *   onGenerate  (companyId) => void — receives the generated id to write into the form
 *   rules       format rules for the id box
 */
export const CompanyIdField = ({ onGenerate, rules }) => {
  const t = useTranslations();
  const required = isCompanyProfileFieldRequired(COMPANY_PROFILE_FIELDS.companyId);

  const handleGenerate = useCallback(() => {
    onGenerate(generateReferenceId());
  }, [onGenerate]);

  return (
    <div className={styles.field}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {t('FORM_COMPANY_ID')}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </Typography>

      <div className={styles.controls}>
        <Field name={COMPANY_PROFILE_FIELDS.companyId} required={required} rules={rules}>
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              className={styles.input}
              id={id}
              name={name}
              aria-label={t('FORM_COMPANY_ID')}
              placeholder={t('FORM_COMPANY_ID_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
            />
          )}
        </Field>

        <Button variant={ButtonVariants.secondary} size={ButtonSizes.md} onClick={handleGenerate}>
          {t('FORM_GENERATE')}
        </Button>
      </div>
    </div>
  );
};
